import type { RequestLogEntry, UserProfile } from "@/types/dashboard";
import { logSourceBucket } from "@/utils/format";
import {
  logFilterHaystack,
  requestLogMethodKey,
  requestLogStatusBand,
  isSystemRequestLogEntry,
  requestLogSourceFilterLabel
} from "@/utils/requestLog";
import type { DashFilterGroup, DashFilterValues } from "@/components/dash-filters/types";
import { boolMapToSelected, selectedToBoolMap } from "@/components/dash-filters/filter-utils";
import type {
  RequestLogFiltersState,
  RequestLogMethodFilter,
  RequestLogSourceFilter
} from "@/features/request-log/useRequestLogPreferences";
import {
  REQUEST_LOG_GROUPS,
  REQUEST_LOG_METHODS,
  REQUEST_LOG_SOURCES,
  REQUEST_LOG_STATUS_BANDS
} from "@/features/request-log/useRequestLogPreferences";

export function requestLogFiltersAreDefault(filters: RequestLogFiltersState): boolean {
  if (filters.staging !== "all") return false;
  if (filters.search.trim()) return false;
  for (const v of Object.values(filters.groups)) {
    if (!v) return false;
  }
  for (const v of Object.values(filters.sources)) {
    if (!v) return false;
  }
  for (const v of Object.values(filters.statusBands)) {
    if (!v) return false;
  }
  for (const v of Object.values(filters.methods)) {
    if (!v) return false;
  }
  return true;
}

export function matchesRequestLogFilters(
  e: RequestLogEntry,
  filters: RequestLogFiltersState,
  opts?: {
    activeSite?: string;
    titlesByPath?: Record<string, string>;
    profileEmail?: (id: string) => string;
    profileFor?: (id: string) => UserProfile | null;
    pageTitle?: string;
  }
): boolean {
  if (isSystemRequestLogEntry(e)) return false;

  const g = String(e.group || "Other");
  if (g === "Page" && !filters.groups.Page) return false;
  if (g === "Asset" && !filters.groups.Asset) return false;
  if (g === "API" && !filters.groups.API) return false;
  if (g === "Other" && !filters.groups.Other) return false;

  const isBot = Boolean(e.isBot);
  if (isBot && !filters.groups.bots) return false;
  if (!isBot && !filters.groups.humans) return false;

  const bucket = logSourceBucket(e) as RequestLogSourceFilter;
  if (bucket && bucket in filters.sources && !filters.sources[bucket as RequestLogSourceFilter]) return false;

  const band = requestLogStatusBand(e.status);
  if (!filters.statusBands[band]) return false;

  const methodKey = requestLogMethodKey(e.method) as RequestLogMethodFilter;
  if (!filters.methods[methodKey]) return false;

  if (filters.staging === "staging" && !e.staging) return false;
  if (filters.staging === "production" && e.staging) return false;

  const q = filters.search.trim().toLowerCase();
  if (!q) return true;

  const prof = e.userId && opts?.profileFor ? opts.profileFor(String(e.userId)) : null;
  const hayEmail =
    prof?.email
      ? String(prof.email)
      : e.userId && opts?.profileEmail
        ? opts.profileEmail(String(e.userId))
        : "";
  const hayName = prof?.name ? String(prof.name) : "";
  const pageTitle = opts?.pageTitle ?? "";
  return logFilterHaystack(e, opts?.activeSite || "", [hayName, hayEmail].filter(Boolean).join(" "), pageTitle).includes(q);
}

export type RequestLogLiveFilterOpts = {
  filters: RequestLogFiltersState;
  activeSite: string;
  titlesByPath: Record<string, string>;
  profileEmail: (id: string) => string;
  profileFor: (id: string) => UserProfile | null;
};

export function matchesRequestLogFiltersWithContext(
  e: RequestLogEntry,
  ctx: RequestLogLiveFilterOpts
): boolean {
  const pageTitle = ctx.titlesByPath[String(e.url || "/").split("?")[0] || "/"] || "";
  return matchesRequestLogFilters(e, ctx.filters, {
    activeSite: ctx.activeSite,
    titlesByPath: ctx.titlesByPath,
    profileEmail: ctx.profileEmail,
    profileFor: ctx.profileFor,
    pageTitle
  });
}

export const REQUEST_LOG_DASH_GROUPS: DashFilterGroup[] = [
  {
    id: "type",
    label: "Type",
    mode: "multi",
    options: REQUEST_LOG_GROUPS.map((g) => ({ id: g, label: g }))
  },
  {
    id: "status",
    label: "Status",
    mode: "multi",
    options: REQUEST_LOG_STATUS_BANDS.map((b) => ({ id: b, label: b }))
  },
  {
    id: "method",
    label: "Method",
    mode: "multi",
    options: REQUEST_LOG_METHODS.map((m) => ({ id: m, label: m === "other" ? "Other" : m }))
  },
  {
    id: "source",
    label: "Source",
    mode: "multi",
    options: REQUEST_LOG_SOURCES.map((s) => ({ id: s, label: requestLogSourceFilterLabel(s) }))
  },
  {
    id: "audience",
    label: "Audience",
    mode: "multi",
    options: [
      { id: "humans", label: "Humans" },
      { id: "bots", label: "Bots" }
    ]
  },
  {
    id: "host",
    label: "Host",
    mode: "single",
    options: [
      { id: "production", label: "Prod" },
      { id: "staging", label: "Staging" }
    ]
  }
];

export function requestLogFiltersToDashValues(filters: RequestLogFiltersState): DashFilterValues {
  return {
    type: boolMapToSelected(
      { Page: filters.groups.Page, Asset: filters.groups.Asset, API: filters.groups.API, Other: filters.groups.Other },
      REQUEST_LOG_GROUPS
    ),
    status: boolMapToSelected(filters.statusBands, REQUEST_LOG_STATUS_BANDS),
    method: boolMapToSelected(filters.methods, REQUEST_LOG_METHODS),
    source: boolMapToSelected(filters.sources, REQUEST_LOG_SOURCES),
    audience: boolMapToSelected(
      { humans: filters.groups.humans, bots: filters.groups.bots },
      ["humans", "bots"] as const
    ),
    host: filters.staging === "all" ? [] : [filters.staging]
  };
}

export function applyDashValuesToRequestLogFilters(
  prev: RequestLogFiltersState,
  values: DashFilterValues
): RequestLogFiltersState {
  const type = selectedToBoolMap(values.type, REQUEST_LOG_GROUPS);
  const audience = selectedToBoolMap(values.audience, ["humans", "bots"] as const);
  const host = values.host?.[0];
  return {
    ...prev,
    groups: {
      ...type,
      humans: audience.humans,
      bots: audience.bots
    },
    statusBands: selectedToBoolMap(values.status, REQUEST_LOG_STATUS_BANDS),
    methods: selectedToBoolMap(values.method, REQUEST_LOG_METHODS),
    sources: selectedToBoolMap(values.source, REQUEST_LOG_SOURCES),
    staging: host === "staging" || host === "production" ? host : "all"
  };
}
